import { useChatApi } from '@/hooks/useChatApi';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/store/chatStore';
import { useConfigStore } from '@/store/configStore';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Message from './chat-elements/Message';
import MessageInput from './chat-elements/MessageInput';
import ChatAlert from './ChatAlert';
import ChatHeader from './ChatHeader';
import EmptyScreen from './EmptyScreen';
import ThreeDotThinkingAnimated from './icons/ThreeDotThinkingAnimated';
import InactivityNotice from './InactivityNotice';

const ChatComponent = () => {
  const { messages, isLoading } = useChatApi();
  const { isOpen, setThreadId, chatSession, showFeedbackAlert } = useChatStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToMessage = useCallback((messageId: string) => {
    const attemptScroll = (retries = 3) => {
      if (!messagesContainerRef.current) return;

      const messageElement = messagesContainerRef.current.querySelector(
        `#${CSS.escape(messageId)}`
      );

      if (!messageElement) {
        if (retries > 0) {
          setTimeout(() => attemptScroll(retries - 1), 50);
        }
        return;
      }

      // Check if the element is actually in the DOM and visible
      if ((messageElement as HTMLElement).offsetHeight === 0) {
        if (retries > 0) {
          setTimeout(() => attemptScroll(retries - 1), 50);
        }
        return;
      }

      // Try different scroll methods
      try {
        // Method 1: standard scrollIntoView
        if ('scrollBehavior' in document.documentElement.style) {
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        } else {
          // Method 2: for Safari/old browsers
          const container = messageElement.closest('.chat-container');
          if (container) {
            const elementTop = (messageElement as HTMLElement).offsetTop;
            container.scrollTo({
              top: elementTop,
              behavior: 'smooth',
            });
          } else {
            messageElement.scrollIntoView({ block: 'start' });
          }
        }
      } catch (error) {
        console.warn('Scroll failed, using fallback:', error);
        // Fallback: simple scroll without animation
        messageElement.scrollIntoView({ block: 'start' });
      }
    };

    // Start with a small delay for reliability
    requestAnimationFrame(() => attemptScroll());
  }, []);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];

    const previousMessage = messages[messages.length - 2];

    const messageToScroll =
      lastMessage?.output?.[0]?.role === 'user' ? lastMessage : previousMessage;

    if (messageToScroll?.id) {
      scrollToMessage(messageToScroll.id);
    }
  }, [messages, scrollToMessage]);

  useEffect(() => {
    const threadId = localStorage.getItem('threadId');

    if ((isOpen && threadId !== chatSession.threadId) || !threadId) {
      if (threadId) {
        setThreadId(threadId);
      } else {
        const newThreadId = uuidv4();
        localStorage.setItem('threadId', newThreadId);
        setThreadId(newThreadId);
      }
    }
  }, [isOpen, setThreadId, chatSession.threadId]);

  const suggestions = useMemo(() => {
    return messages[messages.length - 1]?.suggestions || [];
  }, [messages]);

  const noMessages = messages.length === 0;

  const { config } = useConfigStore();

  const generalSpecificVariant = config.ui?.generalSpecificVariant;

  return (
    <div className="mx-auto flex h-full flex-col items-center">
      {/* Header */}
      <ChatHeader />

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className={cn(
          'chat-container relative flex w-full flex-1 flex-col items-center gap-[16px] overflow-y-auto overflow-x-hidden py-4 sm:gap-[20px] md:mx-[-20px] md:gap-[24px] md:px-[20px]',
          {
            'py-4 md:mx-[-16px] md:px-[16px] lg:mx-[-16px] lg:px-[16px]':
              generalSpecificVariant === 'velio',
          }
        )}
      >
        {noMessages && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-1 items-center justify-center"
          >
            <EmptyScreen />
          </motion.div>
        )}
        {messages.map(message => (
          <Message key={message.id} message={message} />
        ))}
        {isLoading && (
          <Message
            key={messages.length}
            message={{
              id: 'loading-id',
              output: [
                {
                  role: 'system',
                  type: 'reactNode',
                  content: <ThreeDotThinkingAnimated />,
                },
              ],
              messageId: 'loading-id',
              threadId: 'loading-id',
              timestamp: new Date().toISOString(),
            }}
          />
        )}
        {!noMessages && <div ref={messagesEndRef} className="grow" />}
        <div className="sticky bottom-0 left-0 right-0 w-full max-w-[780px] space-y-3">
          <AnimatePresence>
            {showFeedbackAlert && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
              >
                <ChatAlert />
              </motion.div>
            )}
          </AnimatePresence>
          <InactivityNotice />
        </div>
      </div>
      {/* Input */}
      <MessageInput suggestions={suggestions} />
    </div>
  );
};

export default ChatComponent;
